'use client';

/**
 * PRODUCT COVER — couverture SVG procédurale « Or & Émeraude » pour les références
 * du Catalogue Univers (2500 produits) qui n'ont pas de photo réelle.
 * Déterministe : le couple (hue, pat) dérivé de l'id produit génère toujours
 * le même motif arabe — étoile 8 branches, arabesques, rayons, losanges, croissants ou dômes.
 */
import { useMemo } from 'react';

export interface CoverSpec {
  hue: number;
  pat: number;
}

/** Hash déterministe court pour la variation interne. */
function seedOf(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export default function ProductCover({
  id, spec, label, brand, className,
}: {
  id: string;
  spec: CoverSpec;
  label?: string;
  brand?: string;
  className?: string;
}) {
  const { hue, pat } = spec;
  const seed = seedOf(id);

  const bg = `hsl(${(hue + 160) % 360} 42% 9%)`;
  const bg2 = `hsl(${(hue + 190) % 360} 48% 13%)`;
  const goldA = `hsl(${(hue + 45) % 360} 62% 62%)`;
  const stroke = 'rgba(212,175,55,0.55)';
  const strokeSoft = 'rgba(212,175,55,0.28)';

  const initial = useMemo(() => {
    const clean = (label || brand || 'Q').replace(/^[\d\s·]+/, '');
    return clean.trim().charAt(0).toUpperCase() || 'Q';
  }, [label, brand]);

  /* Motifs déterministes — 6 variantes arabesques */
  const pattern = useMemo(() => {
    const els: React.ReactNode[] = [];
    const r = (n: number) => ((seed >> (n % 24)) % 100) / 100; // pseudo-random 0..1
    const cx = 60, cy = 60;

    if (pat === 0) {
      // Étoile 8 branches — motif central + mirettes
      els.push(
        <g key="p0">
          {[0, 45, 90, 135].map((a) => (
            <rect key={a} x={cx - 42} y={cy - 42} width={84} height={84}
              transform={`rotate(${a} ${cx} ${cy})`} fill="none" stroke={strokeSoft} strokeWidth={1} />
          ))}
          <circle cx={cx} cy={cy} r={54} fill="none" stroke={strokeSoft} strokeWidth={1} />
        </g>
      );
    } else if (pat === 1) {
      // Arabesques concentriques
      els.push(
        <g key="p1">
          {[16, 26, 36, 46, 56].map((rr, i) => (
            <circle key={rr} cx={cx + (i % 2 ? 2 : -2)} cy={cy} r={rr} fill="none"
              stroke={i % 2 ? strokeSoft : stroke} strokeWidth={i % 2 ? 0.8 : 1.1}
              strokeDasharray={i % 2 ? undefined : 'none'} />
          ))}
        </g>
      );
    } else if (pat === 2) {
      // Rayons de lumière
      els.push(
        <g key="p2" opacity={0.8}>
          {Array.from({ length: 12 }, (_, i) => {
            const a = (i * 30 + r(i) * 10) * Math.PI / 180;
            return <line key={i} x1={cx + Math.cos(a) * 20} y1={cy + Math.sin(a) * 20}
              x2={cx + Math.cos(a) * 58} y2={cy + Math.sin(a) * 58}
              stroke={i % 3 === 0 ? stroke : strokeSoft} strokeWidth={i % 3 === 0 ? 1.4 : 0.8} />;
          })}
        </g>
      );
    } else if (pat === 3) {
      // Treillis de losanges
      els.push(
        <g key="p3">
          {Array.from({ length: 7 }, (_, i) => (
            <path key={`d${i}`} d={`M ${-10 + i * 20} 60 l 30 -30 l 30 30 l -30 30 Z`}
              fill="none" stroke={strokeSoft} strokeWidth={0.9} />
          ))}
          {Array.from({ length: 7 }, (_, i) => (
            <path key={`e${i}`} d={`M ${-10 + i * 20} 60 l 30 30 l 30 -30 l -30 -30 Z`}
              fill="none" stroke={strokeSoft} strokeWidth={0.9} />
          ))}
        </g>
      );
    } else if (pat === 4) {
      // Croissants et étoiles
      els.push(
        <g key="p4">
          <path d={`M ${cx - 18} ${cy - 30} a 34 34 0 1 0 0 60 a 27 27 0 1 1 0 -60 Z`}
            fill="none" stroke={stroke} strokeWidth={1.2} />
          {Array.from({ length: 9 }, (_, i) => {
            const x = 12 + r(i) * 96, y = 10 + r(i + 7) * 100;
            return <circle key={i} cx={x} cy={y} r={0.9 + r(i + 3)} fill="rgba(247,231,176,0.5)" />;
          })}
        </g>
      );
    } else {
      // Arcades de dômes
      els.push(
        <g key="p5">
          {[0, 1, 2].map((i) => (
            <path key={i} d={`M ${10 + i * 40} 96 v -26 a 16 16 0 0 1 32 0 v 26`}
              fill="none" stroke={i === 1 ? stroke : strokeSoft} strokeWidth={i === 1 ? 1.3 : 0.9} />
          ))}
          <line x1={4} y1={98} x2={116} y2={98} stroke={strokeSoft} strokeWidth={1} />
        </g>
      );
    }
    return els;
  }, [pat, seed, stroke, strokeSoft]);

  return (
    <svg viewBox="0 0 120 120" className={className} role="img" aria-label={label || brand || 'Produit royal'}>
      <defs>
        <linearGradient id={`bg-${seed}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={bg2} />
          <stop offset="55%" stopColor={bg} />
          <stop offset="100%" stopColor="#021510" />
        </linearGradient>
        <radialGradient id={`glow-${seed}`} cx="50%" cy="42%" r="55%">
          <stop offset="0%" stopColor={goldA} stopOpacity={0.30} />
          <stop offset="100%" stopColor={goldA} stopOpacity={0} />
        </radialGradient>
      </defs>

      <rect width="120" height="120" fill={`url(#bg-${seed})`} />
      <rect width="120" height="120" fill={`url(#glow-${seed})`} />
      {pattern}

      {/* Écusson central doré */}
      <circle cx="60" cy="60" r={21} fill="rgba(3,32,25,0.72)" stroke="rgba(212,175,55,0.8)" strokeWidth={1.3} />
      <circle cx="60" cy="60" r={24.5} fill="none" stroke="rgba(212,175,55,0.3)" strokeWidth={0.7} />
      <text x="60" y="60" textAnchor="middle" dominantBaseline="central"
        fontFamily="var(--font-cormorant), serif" fontSize={22} fontWeight={700}
        fill="#f0d98c">{initial}</text>

      {/* Liseré royal */}
      <rect x="3" y="3" width="114" height="114" fill="none" stroke="rgba(212,175,55,0.4)" strokeWidth={1} />
      <rect x="5.5" y="5.5" width="109" height="109" fill="none" stroke="rgba(212,175,55,0.15)" strokeWidth={0.6} />
    </svg>
  );
}
